import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';

interface NavbarProps {
  onLoginClick: () => void;
  onRegisterClick: () => void;
}

export default function Navbar({ onLoginClick, onRegisterClick }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === '/';
  const { user } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    if (!isHome) {
      setScrolled(true);
    }
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isHome]);

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 right-0 left-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-6 md:px-12 lg:px-20">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href="/" className="flex items-center gap-2 group">
            <div className="w-10 h-10 rounded-full bg-carland-dark flex items-center justify-center transition-transform duration-300 group-hover:scale-105">
              <span className="text-white font-pacifico text-lg">C</span>
            </div>
            <span className={`text-xl font-bold transition-colors duration-300 ${scrolled ? 'text-carland-dark' : 'text-white'}`}>
              کارلند
            </span>
          </a>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-10">
            {isHome ? (
              <>
                <button
                  onClick={() => scrollToSection('hero')}
                  className={`text-sm font-medium transition-colors duration-300 whitespace-nowrap cursor-pointer ${
                    scrolled ? 'text-carland-dark hover:text-carland-accent' : 'text-white/90 hover:text-white'
                  }`}
                >
                  صفحه اصلی
                </button>
                <button
                  onClick={() => scrollToSection('services')}
                  className={`text-sm font-medium transition-colors duration-300 whitespace-nowrap cursor-pointer ${
                    scrolled ? 'text-carland-dark hover:text-carland-accent' : 'text-white/90 hover:text-white'
                  }`}
                >
                  خدمات
                </button>
                <button
                  onClick={() => scrollToSection('licenses')}
                  className={`text-sm font-medium transition-colors duration-300 whitespace-nowrap cursor-pointer ${
                    scrolled ? 'text-carland-dark hover:text-carland-accent' : 'text-white/90 hover:text-white'
                  }`}
                >
                  مجوزها
                </button>
                <button
                  onClick={() => scrollToSection('contact')}
                  className={`text-sm font-medium transition-colors duration-300 whitespace-nowrap cursor-pointer ${
                    scrolled ? 'text-carland-dark hover:text-carland-accent' : 'text-white/90 hover:text-white'
                  }`}
                >
                  تماس
                </button>
              </>
            ) : (
              <>
                <a href="/" className={`text-sm font-medium transition-colors duration-300 whitespace-nowrap ${
                  scrolled ? 'text-carland-dark hover:text-carland-accent' : 'text-white/90 hover:text-white'
                }`}>
                  صفحه اصلی
                </a>
                <a href="/register-info" className={`text-sm font-medium transition-colors duration-300 whitespace-nowrap ${
                  scrolled ? 'text-carland-dark hover:text-carland-accent' : 'text-white/90 hover:text-white'
                }`}>
                  راهنمای ثبت‌نام
                </a>
              </>
            )}
          </div>

          {/* Action Buttons */}
          <div className="hidden md:flex items-center gap-3">
            {user ? (
              <a
                href="/member"
                className="px-6 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer transition-all duration-300 bg-carland-dark text-white hover:bg-carland-darker"
              >
                <i className="ri-dashboard-line ml-1.5"></i>
                پنل کاربری
              </a>
            ) : (
              <>
                <button
                  onClick={onLoginClick}
                  className={`px-6 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer transition-all duration-300 border ${
                    scrolled
                      ? 'border-carland-dark text-carland-dark hover:bg-carland-dark hover:text-white'
                      : 'border-white/60 text-white hover:bg-white hover:text-carland-dark'
                  }`}
                >
                  ورود
                </button>
                <button
                  onClick={onRegisterClick}
                  className="px-6 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer transition-all duration-300 bg-carland-dark text-white hover:bg-carland-darker"
                >
                  ثبت‌نام
                </button>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center cursor-pointer transition-colors duration-300 ${
              scrolled ? 'text-carland-dark' : 'text-white'
            }`}
          >
            <i className={`ri-${mobileMenuOpen ? 'close' : 'menu'}-line text-2xl`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden transition-all duration-400 overflow-hidden ${
          mobileMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        } ${scrolled ? 'bg-white' : 'bg-carland-dark/95 backdrop-blur-md'}`}
      >
        <div className="px-6 py-6 flex flex-col gap-4">
          {isHome ? (
            <>
              <button onClick={() => scrollToSection('hero')} className={`text-right text-sm font-medium whitespace-nowrap cursor-pointer ${scrolled ? 'text-carland-dark' : 'text-white'}`}>صفحه اصلی</button>
              <button onClick={() => scrollToSection('services')} className={`text-right text-sm font-medium whitespace-nowrap cursor-pointer ${scrolled ? 'text-carland-dark' : 'text-white'}`}>خدمات</button>
              <button onClick={() => scrollToSection('licenses')} className={`text-right text-sm font-medium whitespace-nowrap cursor-pointer ${scrolled ? 'text-carland-dark' : 'text-white'}`}>مجوزها</button>
              <button onClick={() => scrollToSection('contact')} className={`text-right text-sm font-medium whitespace-nowrap cursor-pointer ${scrolled ? 'text-carland-dark' : 'text-white'}`}>تماس</button>
            </>
          ) : (
            <>
              <a href="/" className={`text-right text-sm font-medium whitespace-nowrap ${scrolled ? 'text-carland-dark' : 'text-white'}`}>صفحه اصلی</a>
              <a href="/register-info" className={`text-right text-sm font-medium whitespace-nowrap ${scrolled ? 'text-carland-dark' : 'text-white'}`}>راهنمای ثبت‌نام</a>
            </>
          )}
          <div className="flex gap-3 pt-2">
            {user ? (
              <a
                href="/member"
                className="flex-1 px-4 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer transition-all duration-300 bg-carland-dark text-white hover:bg-carland-darker text-center"
              >
                <i className="ri-dashboard-line ml-1.5"></i>
                پنل کاربری
              </a>
            ) : (
              <>
                <button
                  onClick={() => { onLoginClick(); setMobileMenuOpen(false); }}
                  className={`flex-1 px-4 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer transition-all duration-300 border text-center ${
                    scrolled
                      ? 'border-carland-dark text-carland-dark hover:bg-carland-dark hover:text-white'
                      : 'border-white/60 text-white hover:bg-white hover:text-carland-dark'
                  }`}
                >
                  ورود
                </button>
                <button
                  onClick={() => { onRegisterClick(); setMobileMenuOpen(false); }}
                  className="flex-1 px-4 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer transition-all duration-300 bg-carland-dark text-white hover:bg-carland-darker text-center"
                >
                  ثبت‌نام
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}